import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'store'
import { postAPI } from 'utils/apiUtil'
import { inputF, promiseVoidF, voidF } from 'types/FunctionTypes'
import { commonMsg } from 'assets/MessageCode'
import * as PageAction from 'slicers/PageSlice'
import * as LoginAction from 'slicers/LoginSlice'
import * as TableAction from 'slicers/TableSlicers/TableSlice'
import * as ModalAction from 'slicers/ModalSlice'
import * as ModalUserAction from 'slicers/ModalUserSlice'
import { reqChangePassword, reqCreatePassword, reqLogin, resLogin } from 'utils/requestUtil'
import { getStrBytes } from 'utils/numberUtil'
import { messageColor } from 'types/ReferTypes'
import { select } from 'types/SearchTypes'
import { keyInfo } from 'types/ReferTypes'

/**
 * ユーザー情報取得
 *  ジャペル判定
 *  スーパーユーザー判定
 * @returns
 */
export const useUser = (): [boolean, boolean] => {
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const isSuper = useSelector((state) => state.LoginReducer.isSuper)
  return [isJapell, isSuper]
}

/**
 * ログイン情報取得
 *   ユーザーコード(メールアドレス、担当者コード)
 *   パスワード
 *   ログインボタン押下処理
 *   ローディング状態
 *   メッセージ
 *   メッセージカラー
 * @returns
 */
export const useLogin = (history: any): [string, inputF, string, inputF, voidF, boolean, string, messageColor] => {
  const dispatch = useDispatch()
  const [ID, setID] = useState('')
  const [PASSWORD, setPASSWORD] = useState('') // パスワードはStoreに貯めない
  const loading = useSelector((state) => state.PageReducer.loading)
  const message = useSelector((state) => state.PageReducer.message)
  const messageColor = useSelector((state) => state.PageReducer.messageColor)

  const changeUserCode = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      setID(e.target.value)
    },
    [ID],
  )
  const changePassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      setPASSWORD(e.target.value)
    },
    [PASSWORD],
  )
  const login = useCallback(async () => {
    dispatch(PageAction.startLoading('ログイン中...'))
    postAPI<reqLogin, resLogin>(
      '/postLoginInfo',
      { ID: ID, PASSWORD: PASSWORD },
      (r) => {
        console.log(r)
        dispatch(PageAction.changeMessage(r))
        if (r.error) return
        const data = r.body
        dispatch(LoginAction.changeLoginInfo(data))
        dispatch(TableAction.format()) // ページネーションなど初期化
        if (data.loginKbn === 1) {
          // 仮ログイン
          sessionStorage.setItem('token', data.token) // アクセストークンの設定
          history.push('/changePassword') //パスワード変更画面に遷移
        } else if (data.loginKbn === 2) {
          // 本ログイン
          sessionStorage.setItem('token', data.token) // アクセストークンの設定
          history.push('/juryohyo') // 販売条件照会画面に遷移
        }
      },
      () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_LOGIN_MSG)),
      () => dispatch(PageAction.endLoading()),
    )
  }, [ID, PASSWORD])

  return [ID, changeUserCode, PASSWORD, changePassword, login, loading, message, messageColor]
}

// 異常発生時にログイン画面に戻す処理 //================================//
export const useErrorLogout = (history: any): [voidF] => {
  const dispatch = useDispatch()
  const backLogin = useCallback(() => {
    dispatch(LoginAction.format())
    dispatch(PageAction.changeMessageError(commonMsg.ERROR_LOGIN_MSG))
    history.push('/') //ログイン画面に遷移
  }, [])
  return [backLogin]
}

/**
 * パスワード・氏名変更画面情報取得
 *   氏名
 *   氏名変更処理
 *   新パスワード
 *   新パスワード変更処理
 *   再入力新パスワード
 *   再入力新パスワード変更処理
 *   変更前後判定
 *   ボタン押下処理
 *   メッセージ
 *   メッセージカラー
 *   ユーザー追加リンク押下処理
 *   ボタン活性判定
 *   仮ログイン判定
 *   使用許諾同意チェック
 *   使用許諾同意チェック変更処理
 * @returns
 */
export const useChangePassword = (
  history: any,
): [
  string,
  inputF, // 氏名
  string,
  inputF, // 新パスワード
  string,
  inputF, // 再入力新パスワード
  boolean,
  promiseVoidF | voidF,
  string,
  messageColor, // メッセージ
  voidF,
  boolean, // ボタン活性判定
  boolean,
  boolean,
  voidF, // 仮ログイン時処理
] => {
  const dispatch = useDispatch()
  const [name, setName] = useState('')
  const [pass, setPass] = useState('')
  const [rePass, setRePass] = useState('')
  const [changed, setChanged] = useState(false)
  const [licenseCheck, setLicenseCheck] = useState(false)
  const MAIL = useSelector((state) => state.LoginReducer.MAIL)
  const loginKbn = useSelector((state) => state.LoginReducer.loginKbn)
  const message = useSelector((state) => state.PageReducer.message)
  const messageColor = useSelector((state) => state.PageReducer.messageColor)
  const loading = useSelector((state) => state.PageReducer.loading)
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const keyInfo = useSelector((state) => state.LoginReducer.keyInfo)

  const isKariLogin = loginKbn === 1
  const noInputName = name.trim() === ''
  const noInputPass = pass.trim() === ''
  const noInputRePass = rePass.trim() === ''

  const noInputAll = noInputName && noInputPass && noInputRePass
  const noInputOne = noInputName || noInputPass || noInputRePass
  const disabled = isKariLogin ? !licenseCheck || noInputOne : noInputAll

  const changeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      // 文字バイトチェック。40バイトまで許可 ※アルファベットのとき25文字までになっているので修正必要
      const value = e.target.value
      if (getStrBytes(value) > 40) return
      setName(value)
      changeMessage(value.trim() === '', noInputPass, noInputRePass)
    },
    [name, message, noInputName, noInputPass, noInputRePass],
  )

  const changePass = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      const value = e.target.value
      setPass(value)
      changeMessage(noInputName, value.trim() === '', noInputRePass)
    },
    [pass, message, noInputName, noInputPass, noInputRePass],
  )

  const changeRePass = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      const value = e.target.value
      setRePass(value)
      changeMessage(noInputName, noInputPass, value.trim() === '')
    },
    [rePass, message, noInputName, noInputPass, noInputRePass],
  )

  const click = useCallback(
    !changed
      ? // 変更前
        async () => {
          if (!loading) {
            dispatch(PageAction.startLoading('処理中...'))
            await postAPI<reqChangePassword, {}>(
              '/postChangePassword',
              { name: name, pass: pass, rePass: rePass, MAIL: MAIL },
              (r) => {
                dispatch(PageAction.changeMessage(r))
                if (!r.error) {
                  setChanged(true)
                  dispatch(LoginAction.changeLoginKbn(0))
                }
              },
              () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_LOGIN_MSG)),
              () => dispatch(PageAction.endLoading()),
            )
          }
        }
      : // 変更後
        () => history.push('/'),
    [changed, name, pass, rePass, noInputName, noInputPass, noInputRePass, loading],
  )

  // ユーザー追加表示処理 //================================//
  const clickAdd = async () => {
    if (!loading) {
      if (!isJapell) {
        dispatch(ModalAction.startLoading('処理中...'))
        await postAPI<{ keyInfo: keyInfo[] }, { tokuiList: select[]; tenList: select[] }>(
          '/postUserAddList',
          { keyInfo: keyInfo },
          (r) => {
            dispatch(ModalUserAction.changeTokuiList(r.body.tokuiList))
            dispatch(ModalUserAction.changeTenList(r.body.tenList))
          },
          () => dispatch(ModalAction.changeMessageError(commonMsg.ERROR_MSG)),
          () => dispatch(ModalAction.endLoading()),
        )
      }
      dispatch(ModalAction.open())
    }
  }

  const changeLicenseCheck = useCallback(() => {
    setLicenseCheck(!licenseCheck)
    changeMessage(noInputName, noInputPass, noInputRePass)
  }, [licenseCheck, noInputName, noInputPass, noInputRePass])

  const changeMessage = (noInputName: boolean, noInputPass: boolean, noInputRePass: boolean) => {
    let allOK = true
    if (noInputPass) {
      dispatch(PageAction.changeMessageError('パスワードを入力してください。'))
      allOK = false
    }
    if (!noInputPass && noInputRePass) {
      dispatch(PageAction.changeMessageError('再入力パスワードを入力してください。'))
      allOK = false
    }
    if (noInputName) {
      dispatch(PageAction.changeMessageError('氏名を入力してください。'))
      allOK = false
    }
    if (noInputName && noInputPass && noInputRePass) {
      dispatch(PageAction.changeMessageError('パスワード、氏名を入力してください。'))
      allOK = false
    }
    if (allOK) {
      dispatch(PageAction.deleteMessage())
    }
  }

  return [
    name,
    changeName, //
    pass,
    changePass,
    rePass,
    changeRePass,
    changed,
    click,
    message,
    messageColor,
    clickAdd,
    disabled,
    isKariLogin,
    licenseCheck,
    changeLicenseCheck,
  ]
}

/**
 * パスワード再発行画面情報取得
 *   メールアドレス
 *   メールアドレス変更処理
 *   変更前後判定
 *   ボタン押下処理
 *   メッセージ
 *   メッセージカラー
 *   ボタン活性判定
 * @returns
 */
export const useCreatePassword = (
  history: any,
): [
  string,
  inputF, // メールアドレス
  boolean,
  promiseVoidF | voidF,
  string,
  messageColor,
  boolean, // ボタン活性判定
] => {
  const dispatch = useDispatch()
  const [mail, setMail] = useState('')
  const [changed, setChanged] = useState(false)
  const message = useSelector((state) => state.PageReducer.message)
  const messageColor = useSelector((state) => state.PageReducer.messageColor)
  const loading = useSelector((state) => state.PageReducer.loading)

  const disabled = mail.trim() === ''

  const changeMail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      setMail(e.target.value)
      if (message !== '') {
        // エラーメッセージ出ているとき
        if (!disabled) dispatch(PageAction.deleteMessage())
      }
    },
    [mail, message, disabled],
  )

  const click = useCallback(
    !changed
      ? // 変更前
        async () => {
          if (!loading) {
            dispatch(PageAction.startLoading('処理中...'))
            await postAPI<reqCreatePassword, {}>(
              '/postCreatePassword',
              { mail: mail },
              (r) => {
                dispatch(PageAction.changeMessage(r))
                if (!r.error) {
                  setChanged(true)
                  dispatch(LoginAction.changeLoginKbn(0))
                }
              },
              () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_LOGIN_MSG)),
              () => dispatch(PageAction.endLoading()),
            )
          }
        }
      : // 変更後
        () => history.push('/'),
    [changed, mail, loading],
  )

  return [
    mail,
    changeMail, //
    changed,
    click,
    message,
    messageColor,
    disabled,
  ]
}
